import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, ShoppingBag, Check, X, Sparkles, Users, Mail, ClipboardCheck } from "lucide-react";
import PhoneCodeInput from "../loyalty/PhoneCodeInput";
import PhoneCountryInput from "../PhoneCountryInput";
import { API } from "../../lib/api";
import { useSaleCountdown } from "../../lib/saleCountdown";
import { cleanText, cleanPrefill } from "../../lib/razorpayOptions";
import { InlineVideoEmbed } from "../InlineVideoEmbed";
import { LoyaltyCoinPanel } from "./LoyaltyCoinPanel";
import { trackStorefront } from "./_shared";
import CoinEarnChip from "../CoinEarnChip";
import useCreatorLoyalty from "../../hooks/useCreatorLoyalty";

/**
 * StorefrontBuyModal — the shared checkout modal used by both the
 * storefront grid (`pages/Storefront.jsx > StorefrontCard`) and the
 * focused single-product sell page (`pages/SellPage.jsx`). Handles
 * units/booking → coupon apply → loyalty-coin redemption → phone-OTP
 * gate (if the creator requires it) → Razorpay order+verify.
 *
 * Uses the same order/verify endpoints as ProductBlock — kept close to
 * the storefront to avoid a circular dep with ActionBlockRenderer.jsx.
 *
 * Extracted from `pages/Storefront.jsx` during the v142.3 refactor.
 * Behaviour is unchanged; only the file location changed.
 */
export function StorefrontBuyModal({ product, slug, requireOtp, otpChannel, codEnabled = false, onlineEnabled = true, requirePhone = false, requireAddress = false, deliveryCharge = 0, onClose, onSuccess, initialCouponCode, buyerUser }) {
  const [email, setEmail] = useState(buyerUser?.email || "");
  const [name, setName] = useState(buyerUser?.name || "");
  // v145.25 — Auto-fill phone from the signed-in user profile too so
  // buyers don't re-type known details. Falls back to empty for
  // anonymous buyers or users who haven't added a phone yet.
  const [phone, setPhone] = useState(buyerUser?.phone || "");
  // v146 — Payment method state. Default "razorpay" so existing
  // storefronts (COD off) behave exactly as before. Only exposed in
  // the UI when the creator has enabled COD in their Settings →
  // Commerce & Payments card.
  // v149.70 — A "Cash on Delivery only" shop (`settings.online_enabled`
  // false): COD is the method, and there is nothing to choose.
  const codOnly = codEnabled && !onlineEnabled;
  const [paymentMethod, setPaymentMethod] = useState(codOnly ? "cod" : "razorpay");
  useEffect(() => { if (codOnly) setPaymentMethod("cod"); }, [codOnly]);
  // v145.25 — When auth resolves after mount (session fetch races the
  // modal render), backfill any still-blank fields so the user isn't
  // asked for details we already have.
  useEffect(() => {
    if (!buyerUser) return;
    if (buyerUser.email) setEmail((v) => v || buyerUser.email);
    if (buyerUser.name) setName((v) => v || buyerUser.name);
    if (buyerUser.phone) setPhone((v) => v || buyerUser.phone);
  }, [buyerUser?.user_id]);
  const [address, setAddress] = useState("");
  const [otpCode, setOtpCode] = useState("");
  // otpStage: skip | prompt | sending | entering | verifying | verified
  // v147.58 — Tickets ALWAYS require phone OTP (see server-side force
  // in product_buy/checkout.py). The UI mirrors that so the buyer sees
  // the OTP block even if the storefront's `require_phone_otp` toggle
  // is off. NOTE: `isTicket` is declared here (top of scope) so we can
  // reference it in the `effectiveRequireOtp` init below without TDZ.
  const isService = String(product.kind || "product").toLowerCase() === "service";
  const isTicket = String(product.kind || "product").toLowerCase() === "ticket";
  // v147.99.9 — Buyer already OTP'd their phone at login? Skip the
  // second OTP on the checkout modal. Kills the "OTP twice" complaint:
  // once at login, again at checkout. The backend mirrors this
  // shortcut (session-authed user with phone_verified=True is treated
  // as verified_phone) so the round-trip stays honest — no forged
  // client claim can bypass it.
  const alreadyPhoneVerified = !!(buyerUser?.phone_verified && buyerUser?.phone);
  const effectiveRequireOtp = (!!requireOtp || isTicket) && !alreadyPhoneVerified;
  const [otpStage, setOtpStage] = useState(
    effectiveRequireOtp ? "prompt" : (alreadyPhoneVerified ? "verified" : "skip"),
  );
  const [otpChannelSent, setOtpChannelSent] = useState(null);
  const [verificationToken, setVerificationToken] = useState(null);
  const [units, setUnits] = useState(1);
  const [step, setStep] = useState("form");
  const [error, setError] = useState("");
  const [couponCode, setCouponCode] = useState(initialCouponCode || "");
  const [coupon, setCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [paidOrder, setPaidOrder] = useState(null);
  const [coinPreview, setCoinPreview] = useState(null);
  const [coinsToApply, setCoinsToApply] = useState(0);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const tiers = Array.isArray(product.ticket_tiers) ? product.ticket_tiers : [];
  const hasTiers = isTicket && tiers.length > 0;
  // v147.64 — Auto-select the first available (non-sold-out) tier so
  // the buyer never sees a "pick a tier" 400 from the server just
  // because the picker rendered above their scroll position.
  const _defaultTierId = (() => {
    for (const t of tiers) {
      const left = Number(t.total_tickets || 0) - Number(t.tickets_sold || 0);
      if (left > 0) return t.tier_id;
    }
    return tiers[0]?.tier_id || null;
  })();
  const [selectedTierId, setSelectedTierId] = useState(() => _defaultTierId);
  const selectedTier = hasTiers
    ? (tiers.find(t => t.tier_id === selectedTierId) || tiers[0])
    : null;
  const ticketsSold = Number(product.tickets_sold || 0);
  const totalTickets = Number(product.total_tickets || 0);
  // Effective capacity: tier-scoped when tiers exist, else product-wide.
  const effectiveTotal = selectedTier
    ? Number(selectedTier.total_tickets || 0)
    : totalTickets;
  const effectiveSold = selectedTier
    ? Number(selectedTier.tickets_sold || 0)
    : ticketsSold;
  const ticketsLeft = effectiveTotal > 0
    ? Math.max(0, effectiveTotal - effectiveSold)
    : null;
  const maxPerOrder = Number(product.max_per_order || 10);
  const ticketMaxUnits = ticketsLeft != null
    ? Math.min(maxPerOrder, ticketsLeft) || 1
    : maxPerOrder;
  const isTicketSoldOut = isTicket && ticketsLeft === 0;
  // v147.59 — Effective unit price uses the picked tier's price when
  // tiers are configured, else falls back to the product's flat price.
  const ticketUnitPrice = selectedTier
    ? Number(selectedTier.price || 0)
    : Number(product.price || 0);
  // v147.59 — Auto group discount info (server enforces + computes;
  // this is the preview shown inside the buy modal).
  const groupPct = isTicket ? Number(product.group_discount_pct || 0) : 0;
  const groupMin = isTicket ? Number(product.group_discount_min_qty || 0) : 0;
  const availableSlots = Array.isArray(product.slots) && product.slots.length
    ? product.slots
    : (isService ? ["10:00 AM", "12:00 PM", "3:00 PM", "6:00 PM"] : []);
  const availableDays = Number(product.available_days) > 0
    ? Number(product.available_days) : 30;
  const [bookingDate, setBookingDate] = useState("");
  const [bookingSlot, setBookingSlot] = useState("");
  // v144.27 — When the creator has set `max_per_slot`, we fetch the
  // remaining seat count for each slot on the chosen date so the
  // picker can grey out full ones before the buyer submits.
  const [slotAvail, setSlotAvail] = useState(null);
  useEffect(() => {
    if (!isService || !bookingDate || !product.product_id) { setSlotAvail(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/public/products/${encodeURIComponent(product.product_id)}/slot-availability?date=${encodeURIComponent(bookingDate)}`);
        if (!res.ok) throw new Error("bad response");
        const data = await res.json();
        if (!cancelled) setSlotAvail(data);
      } catch { if (!cancelled) setSlotAvail(null); }
    })();
    return () => { cancelled = true; };
  }, [isService, bookingDate, product.product_id]);
  const slotStatus = useMemo(() => {
    const map = new Map();
    if (slotAvail?.slots) {
      slotAvail.slots.forEach((s) => map.set(s.slot, s));
    }
    return map;
  }, [slotAvail]);
  // v144.27 — Use local-date parts (not toISOString which is UTC) so buyers
  // in positive-offset timezones can pick "today" without the min-date
  // silently shifting back one day.
  const localISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const today = useMemo(() => localISO(new Date()), []);
  const maxDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + availableDays);
    return localISO(d);
  }, [availableDays]);
  // v147.53 — Optional one-time availability window pinned by the
  // creator. When set, the Buy/Book button is disabled outside the
  // window and — for services — the date picker is clamped to it.
  const winFrom = (product.available_from || "").slice(0, 10) || null;
  const winTo = (product.available_to || "").slice(0, 10) || null;
  const winState = useMemo(() => {
    if (!winFrom && !winTo) return { status: "unlimited" };
    if (winFrom && today < winFrom) return { status: "not_started", start: winFrom, end: winTo || winFrom };
    if (winTo && today > winTo) return { status: "ended", start: winFrom || winTo, end: winTo };
    return { status: "active", start: winFrom, end: winTo };
  }, [winFrom, winTo, today]);
  const availabilityBlocked = winState.status === "not_started" || winState.status === "ended";
  const bookingDateMin = winFrom && winFrom > today ? winFrom : today;
  const bookingDateMax = winTo && winTo < maxDate ? winTo : maxDate;
  // v147.55 — Single-day window locks the picker; when the range is a
  // real span, we default the picker to `today` (if inside the range)
  // or the earliest allowed day (if today is before the window). This
  // sits alongside the existing min/max clamp so buyers land on a
  // usable date without any manual selection.
  const isSingleDayWindow = !!(winFrom && winTo && winFrom === winTo);
  useEffect(() => {
    if (!isService) return;
    // Skip if the buyer has already picked something inside the window.
    if (bookingDate && bookingDate >= bookingDateMin && bookingDate <= bookingDateMax) return;
    // Preferred default: today when it falls inside the window;
    // otherwise the window's start (or, if there's no start, today).
    const seed = today >= bookingDateMin && today <= bookingDateMax
      ? today
      : bookingDateMin;
    if (seed) setBookingDate(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isService, bookingDateMin, bookingDateMax, isSingleDayWindow]);
  const formatWinDate = (iso) => {
    if (!iso) return "";
    try {
      const [y, m, d] = iso.split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString(undefined, {
        month: "short", day: "numeric", year: "numeric",
      });
    } catch { return iso; }
  };
  const availabilityLabel = useMemo(() => {
    if (winState.status === "unlimited") return "";
    if (winState.status === "not_started") {
      return winTo && winTo !== winFrom
        ? `Available from ${formatWinDate(winFrom)} to ${formatWinDate(winTo)}`
        : `Available on ${formatWinDate(winFrom)}`;
    }
    if (winState.status === "ended") {
      return `${isService ? "Bookings" : "Sale"} ended on ${formatWinDate(winTo)}`;
    }
    // active
    if (winFrom && winTo && winFrom !== winTo) {
      return `Available ${formatWinDate(winFrom)} – ${formatWinDate(winTo)}`;
    }
    if (winFrom && winTo && winFrom === winTo) {
      return `Available today only (${formatWinDate(winFrom)})`;
    }
    if (winFrom) return `Available from ${formatWinDate(winFrom)}`;
    if (winTo) return `Available until ${formatWinDate(winTo)}`;
    return "";
  }, [winState, winFrom, winTo, isService]);
  // v147.54 — Live countdown when the sale ends today. Silent otherwise.
  const countdown = useSaleCountdown(winState.status === "active" ? winTo : null);
  // v111 — Service = 1 unit per booking. v147.58 — Tickets can be
  // multiple per order (bounded by max_per_order + tickets_left).
  const effectiveUnits = isService
    ? 1
    : isTicket
      ? Math.max(1, Math.min(ticketMaxUnits, Number(units) || 1))
      : Math.max(1, Number(units) || 1);
  // v147.59 — Tickets use the tier price (if any) as the unit price.
  const effectiveUnitPrice = isTicket ? ticketUnitPrice : Number(product.price || 0);
  const subtotal = Math.max(0, effectiveUnitPrice * effectiveUnits);
  // v145.61 — Creator pass-on discount (from affiliate pick). When
  // present, coupons + loyalty coins are disabled (decision 4c) — the
  // Linkola price the buyer sees IS the final price minus optional
  // taxes/shipping. `list_price_inr` is the shop price for display.
  const hasCreatorDiscount = !!product.is_affiliate
    && Number(product.customer_discount_pct || 0) > 0
    && Number(product.list_price_inr || 0) > Number(product.price || 0);
  const shopSubtotal = hasCreatorDiscount
    ? Math.max(0, Number(product.list_price_inr || 0) * effectiveUnits)
    : subtotal;
  const creatorSavings = hasCreatorDiscount ? Math.max(0, shopSubtotal - subtotal) : 0;
  // v145.62 — "Best Deal on Linkola" ribbon triggers when the
  // savings percentage the buyer actually gets exceeds 15%.
  const savingsPct = hasCreatorDiscount && shopSubtotal > 0
    ? Math.round((creatorSavings / shopSubtotal) * 100)
    : 0;
  const showBestDealRibbon = hasCreatorDiscount && savingsPct >= 15;
  // v145.62 — Social-proof buyer count for affiliate/picked products.
  const socialProofCount = Number(product.social_proof_count || 0);
  const showSocialProof = !!product.is_affiliate && socialProofCount > 0;
  const discount = coupon?.discount_inr || 0;
  // v147.59 — Auto group discount preview. Applied BEFORE coupons in
  // the server, so we mirror that here for the visible total. Coupon
  // gating is enforced server-side (400 if buyer tries both).
  const groupDiscountActive = isTicket && groupPct > 0 && groupMin >= 2 && effectiveUnits >= groupMin;
  const groupDiscountInr = groupDiscountActive
    ? Math.round(subtotal * (groupPct / 100) * 100) / 100
    : 0;
  const subtotalAfterGroup = Math.max(0, subtotal - groupDiscountInr);
  const subtotalAfterCoupon = Math.max(0, subtotalAfterGroup - discount);
  // v142.1 — Coin discount computed client-side using the same cap the
  // server enforces (max_coins_by_cap). We can't rely on
  // `coinPreview.discount_inr` here because the panel calls
  // /preview-redeem once with apply_coins=0 (to learn caps + balance),
  // so `discount_inr` from that response is always 0 regardless of the
  // slider position. The server re-enforces the cap on order create.
  const coinDiscount = coinPreview && coinsToApply > 0
    ? Math.min(
        coinsToApply,
        coinPreview.max_coins_by_cap || coinsToApply,
        coinPreview.balance || coinsToApply,
      ) * (coinPreview.value_per_coin_inr || 0)
    : 0;
  const total = Math.max(0, subtotalAfterCoupon - coinDiscount);
  // v146.3 — Delivery / shipping charge from the block config. Applied
  // ONLY to non-service products (services have nothing to ship). The
  // server re-caps this at [0, 10000] and zero's it for services too,
  // so client tampering can't bypass either rule.
  const effectiveDelivery = !isService ? Math.max(0, Number(deliveryCharge) || 0) : 0;
  const grandTotal = Math.max(1, Math.round((total + effectiveDelivery) * 100) / 100);

  // If the visitor edits units after applying a coupon, drop the coupon so
  // the discount doesn't stick to a stale subtotal.
  useEffect(() => {
    if (coupon) {
      setCoupon(null);
      setCouponError("");
    }
  }, [units]);

  const applyCoupon = async () => {
    setCouponError("");
    if (!couponCode.trim()) { setCouponError("Enter a coupon code"); return; }
    setApplyingCoupon(true);
    try {
      const res = await fetch(`${API}/public/products/${encodeURIComponent(product.product_id)}/apply-coupon`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          coupon_code: couponCode.trim(),
          units: Math.max(1, Math.min(100, Number(units) || 1)),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Coupon not applicable");
      setCoupon(data);
      setCouponError("");
    } catch (e2) {
      setCoupon(null);
      setCouponError(e2.message || "Coupon not applicable");
    } finally {
      setApplyingCoupon(false);
    }
  };
  const removeCoupon = () => {
    setCoupon(null);
    setCouponCode("");
    setCouponError("");
  };

  // Fire a `cart_open` event when the modal mounts so the analytics
  // funnel sees checkout intent. Guarded with a ref so React 18
  // StrictMode double-invoke can't fire twice per open.
  const trackedOpen = useRef(false);
  useEffect(() => {
    if (trackedOpen.current) return;
    trackedOpen.current = true;
    trackStorefront(slug, "cart_open", product.product_id);
  }, [slug, product.product_id]);

  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const sendOtp = async () => {
    setError("");
    if (!phone.trim()) { setError("Enter your phone number"); return; }
    setOtpStage("sending");
    try {
      const res = await fetch(`${API}/public/storefront/${encodeURIComponent(slug)}/otp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: phone.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Could not send OTP");
      setOtpChannelSent(data.channel || otpChannel || "auto");
      setOtpStage("entering");
    } catch (e2) {
      setError(e2.message || "Could not send OTP");
      setOtpStage("prompt");
    }
  };

  const verifyOtp = async () => {
    setError("");
    if (!otpCode.trim()) { setError("Enter the OTP you received"); return; }
    setOtpStage("verifying");
    try {
      const res = await fetch(`${API}/public/storefront/${encodeURIComponent(slug)}/otp/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: phone.trim(), code: otpCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Invalid OTP");
      setVerificationToken(data.verification_token);
      setOtpStage("verified");
    } catch (e2) {
      setError(e2.message || "Invalid OTP");
      setOtpStage("entering");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    // v147.56 — Never disable the Pay button just because of an
    // unverified OTP / missing field. Instead surface a clear toast +
    // inline error on tap so the buyer knows exactly what's missing.
    // The prior UX left buyers staring at a greyed-out button with no
    // explanation.
    if (effectiveRequireOtp && !verificationToken && paymentMethod !== "cod") {
      const msg = "Please verify your phone number to continue.";
      setError(msg);
      try { (await import("sonner")).toast.error(msg); } catch (_) {}
      // Scroll the OTP block into view so the buyer sees it without hunting.
      try {
        document.querySelector('[data-testid="storefront-buy-otp-send"], [data-testid="storefront-buy-otp-verify"]')
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch (_) {}
      return;
    }
    if (paymentMethod === "cod") {
      if (!phone.trim()) {
        const msg = "Add a phone number so the seller can reach you.";
        setError(msg);
        try { (await import("sonner")).toast.error(msg); } catch (_) {}
        return;
      }
      // v149.36 — Only for something that gets shipped. The address
      // field is deliberately not rendered for a service (see the
      // `!isService` guard on the textarea below), so requiring one here
      // left a COD service booking with an error it had no way to clear.
      // Matches the `requireAddress && !isService` rule further down.
      if (!isService && !address.trim()) {
        const msg = "Add a delivery address for Cash on Delivery.";
        setError(msg);
        try { (await import("sonner")).toast.error(msg); } catch (_) {}
        return;
      }
    }
    // v146.2 — Block-level checkout requirements. Creators can flip these
    // on for storefront / affiliate blocks so buyers can't skip past a
    // needed phone / delivery address before payment. Address is only
    // enforced for physical (non-service) products.
    if (requirePhone && !phone.trim()) {
      const msg = "Phone number is required to complete this checkout.";
      setError(msg);
      try { (await import("sonner")).toast.error(msg); } catch (_) {}
      return;
    }
    if (requireAddress && !isService && !address.trim()) {
      const msg = "Delivery address is required to complete this checkout.";
      setError(msg);
      try { (await import("sonner")).toast.error(msg); } catch (_) {}
      return;
    }
    if (isService && (!bookingDate || !bookingSlot)) {
      const msg = "Please pick a date and time slot for your booking.";
      setError(msg);
      try { (await import("sonner")).toast.error(msg); } catch (_) {}
      return;
    }
    setStep("processing");
    try {
      const orderRes = await fetch(`${API}/public/products/${encodeURIComponent(product.product_id)}/order`, {
        method: "POST",
        // v145.25 — Send auth so the (still-public) order endpoint can
        // sniff a signed-in buyer and bind coin redemption to their
        // real user_id (see checkout.py). `credentials: include` sends
        // the session cookie; Bearer header covers the localStorage
        // token path used on capacitor / cross-origin browser setups.
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(typeof localStorage !== "undefined" && localStorage.getItem("creatoraiq_session_token")
            ? { Authorization: `Bearer ${localStorage.getItem("creatoraiq_session_token")}` }
            : {}),
        },
        body: JSON.stringify({
          buyer_email: email.trim(),
          buyer_name: name.trim() || undefined,
          buyer_phone: phone.trim() || undefined,
          buyer_address: address.trim() || undefined,
          units: effectiveUnits,
          verification_token: verificationToken || undefined,
          coupon_code: coupon?.code || undefined,
          booking_date: isService ? bookingDate : undefined,
          booking_slot: isService ? bookingSlot : undefined,
          // v147.59 — Selected ticket tier (only for multi-tier events).
          tier_id: hasTiers ? (selectedTier?.tier_id || selectedTierId) : undefined,
          // v146 — Payment method. Backend re-validates the plan +
          // storefront toggle before creating a COD order.
          payment_method: paymentMethod,
          // v116 — Affiliate attribution: product.affiliate_ref is set
          // when the product is being surfaced through an affiliate's
          // bio hub / action page. `pick_id` is preferred; ?ref= URL
          // param is the fallback for shared links.
          affiliate_ref: product.affiliate_ref || (typeof window !== "undefined"
            ? (new URLSearchParams(window.location.search).get("ref") || undefined)
            : undefined),
          pick_id: product.pick_id || undefined,
          // v142.1 — Coin redemption. Server re-caps by max_discount_pct
          // so we can safely echo the user's slider value here.
          apply_coins: coinsToApply > 0 ? coinsToApply : undefined,
          // v146.3 — Block-configured delivery / shipping charge.
          // Server clamps to [0, 10000] and zero's for services.
          delivery_charge_inr: effectiveDelivery > 0 ? effectiveDelivery : undefined,
        }),
      });
      const orderData = await orderRes.json();
      if (!orderRes.ok) throw new Error(orderData?.detail || "Could not create order");
      // v146 — COD short-circuit. Backend has already persisted the
      // pending order; skip Razorpay entirely and show a friendly
      // "awaiting seller confirmation" success screen.
      if (orderData.cod) {
        try { window.dispatchEvent(new CustomEvent("coins:changed")); } catch { /* noop */ }
        // v149.36 — Carry the order the backend just wrote into the
        // confirmation, the same as the Razorpay path, so the buyer sees
        // their order number without hunting for an email.
        setPaidOrder({
          order_id: orderData.order_id,
          amount_inr: orderData.amount_inr ?? grandTotal,
          buyer_email: email,
          buyer_name: name,
          units: effectiveUnits,
          payment_method: "cod",
        });
        setStep("cod_success");
        return;
      }
      const { order_id, order } = orderData;
      const verify = async (payment_id, signature) => {
        const vres = await fetch(`${API}/public/products/${encodeURIComponent(product.product_id)}/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            order_id,
            razorpay_order_id: order.id,
            razorpay_payment_id: payment_id,
            razorpay_signature: signature,
          }),
        });
        const vdata = await vres.json();
        if (!vres.ok) throw new Error(vdata?.detail || "Verify failed");
        // v145.22 — Fire the global coins:changed event so the header
        // pill refreshes after checkout (either the buyer redeemed
        // coins for a discount OR earned coins for the purchase).
        try { window.dispatchEvent(new CustomEvent("coins:changed")); } catch { /* noop */ }
        // v146.95 — Show an in-modal receipt / success screen so the
        // buyer gets an obvious confirmation before we call onSuccess.
        // The parent's onSuccess() (which typically dismisses the modal)
        // is now deferred to the buyer clicking "Done" on the success
        // screen — otherwise they leave the flow with no visible proof
        // of purchase.
        setPaidOrder(vdata?.order || null);
        setStep("success");
      };
      if (order.mock) {
        await verify(`mock_pay_${Date.now()}`, undefined);
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load payment SDK");
      // v146.36 — Razorpay's checkout SDK runs strict regex validation
      // on several `options` fields and throws "The string did not
      // match the expected pattern." when any of them contains
      // characters outside its allow-list. Sanitise via the shared
      // helpers so the modal never fails to open with a cryptic error.
      const rz = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Linkola",
        description: cleanText(product.title || "Product purchase"),
        order_id: order.id,
        prefill: cleanPrefill({ email, name, contact: phone }),
        handler: async (resp) => {
          try { await verify(resp.razorpay_payment_id, resp.razorpay_signature); }
          catch (e2) { setError(e2.message || "Verify failed"); setStep("form"); }
        },
        modal: { ondismiss: () => setStep("form") },
      });
      rz.open();
    } catch (e2) {
      setError(e2.message || "Something went wrong");
      setStep("form");
    }
  };

  // Gate the Pay button when the creator requires OTP.
  const otpVerified = !effectiveRequireOtp || otpStage === "verified";

  return (
    createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchEnd={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="storefront-buy-modal"
    >
      <form
        onSubmit={submit}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        className="bg-white border-4 border-ink shadow-brutal w-full sm:max-w-sm h-[92dvh] sm:h-auto sm:max-h-[92dvh] overflow-y-auto overflow-x-hidden p-5 pt-12 sm:pt-5 space-y-3 rounded-t-lg sm:rounded-none relative"
      >
        {/* v146.68 — Sticky close X so the buyer can always dismiss the
            modal even while scrolled inside a tall form. Previously the
            only "Cancel" button lived at the bottom which was often
            below the fold on tall mobile checkouts (with coupon +
            address + phone-OTP panels stacked). */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          aria-label="Close"
          data-testid="storefront-buy-close"
          className="sticky top-0 float-right -mt-2 -mr-2 z-20 border-2 border-ink bg-white w-8 h-8 inline-flex items-center justify-center shadow-brutalSm hover:bg-coral hover:text-white"
        >
          <X className="w-4 h-4" strokeWidth={3} />
        </button>
        {/* v145.62 — "Best Deal on Linkola" ribbon. Fires when the
            creator has passed on ≥15% of the shop price so shoppers
            instantly feel the win vs. buying direct. Corner-tacked,
            with a subtle diagonal so it reads like a real ribbon. */}
        {showBestDealRibbon && (
          <div
            className="absolute -top-2 -right-2 z-10 pointer-events-none"
            data-testid="storefront-buy-best-deal-ribbon"
          >
            <div className="border-2 border-ink bg-coral text-white px-3 py-1 shadow-brutalSm rotate-3 inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" strokeWidth={3} />
              <span className="font-display font-black text-[11px] uppercase tracking-wider">
                Best deal on Linkola · {savingsPct}% off
              </span>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-5 h-5" strokeWidth={2.5} />
          <h3 className="font-black text-lg">
            {isTicket ? "Get tickets" : isService ? "Book" : "Buy"} {product.title}
          </h3>
        </div>
        {/* v145.62 — Social proof chip on affiliate/picked products. */}
        {showSocialProof && (
          <div
            className="border-2 border-ink bg-mint px-2 py-1 inline-flex items-center gap-1.5 text-[11px] font-bold shadow-brutalSm w-fit"
            data-testid="storefront-buy-social-proof"
          >
            <Users className="w-3 h-3" strokeWidth={2.5} />
            <span>{socialProofCount.toLocaleString("en-IN")} customer{socialProofCount === 1 ? "" : "s"} bought this on Linkola</span>
          </div>
        )}
        {hasCreatorDiscount ? (
          // v145.61 — When the creator has passed on some commission,
          // spell out the win vs. the shop price front-and-centre.
          <div className="border-2 border-ink bg-sun p-2.5 space-y-0.5 text-[11px]" data-testid="storefront-buy-creator-discount">
            <div className="flex items-baseline justify-between">
              <span className="line-through text-ink/60 tabular-nums">Shop price ₹{shopSubtotal.toFixed(2)}</span>
              <span className="font-black uppercase text-[9px] tracking-wider border-2 border-ink bg-white px-1 py-0.5">
                {product.customer_discount_pct}% off
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="font-black text-coral">Linkola price ₹{total.toFixed(2)}</span>
              <span className="font-black text-emerald-700">You save ₹{creatorSavings.toFixed(0)}</span>
            </div>
            {effectiveDelivery > 0 && (
              <div className="flex items-baseline justify-between pt-1 border-t-2 border-ink/20" data-testid="storefront-buy-delivery-line-affiliate">
                <span>+ delivery</span>
                <span className="tabular-nums">₹{effectiveDelivery.toFixed(2)}</span>
              </div>
            )}
            {effectiveDelivery > 0 && (
              <div className="flex items-baseline justify-between font-black">
                <span>Total</span>
                <span className="tabular-nums">₹{grandTotal.toFixed(2)}</span>
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-ink/70">
            ₹{Number(product.price).toFixed(2)}{isService ? "" : <> × <span className="font-bold">{effectiveUnits}</span></>} = <b>₹{subtotal.toFixed(2)}</b>
            {coupon && (
              <>
                <br />
                <span className="text-mint font-bold">
                  − ₹{Number(coupon.discount_inr).toFixed(2)} ({coupon.code} · {coupon.discount_pct}% off)
                </span>
              </>
            )}
            {coinDiscount > 0 && (
              <>
                <br />
                <span className="text-sun font-bold" data-testid="storefront-buy-coin-discount">
                  − ₹{coinDiscount.toFixed(2)} ({coinsToApply} {coinPreview?.coin_name || "coins"})
                </span>
              </>
            )}
            {effectiveDelivery > 0 && (
              <>
                <br />
                <span className="font-bold" data-testid="storefront-buy-delivery-line">
                  + ₹{effectiveDelivery.toFixed(2)} delivery
                </span>
              </>
            )}
            {(coupon || coinDiscount > 0 || effectiveDelivery > 0) && (
              <>
                <br />
                <b>Total ₹{grandTotal.toFixed(2)}</b>
              </>
            )}
          </p>
        )}
        {isTicket ? (
          <TicketEventPanel
            product={product}
            tiers={tiers}
            selectedTierId={selectedTier?.tier_id || selectedTierId}
            onSelectTier={(id) => { setSelectedTierId(id); setUnits(1); }}
            units={units}
            setUnits={setUnits}
            maxUnits={ticketMaxUnits}
            ticketsLeft={ticketsLeft}
            isTicketSoldOut={isTicketSoldOut}
            groupPct={groupPct}
            groupMin={groupMin}
            groupDiscountActive={groupDiscountActive}
            groupDiscountInr={groupDiscountInr}
          />
        ) : isService ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="storefront-buy-booking-panel">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider block mb-1">Date</label>
              <input
                type="date" required
                min={bookingDateMin} max={bookingDateMax}
                value={bookingDate}
                onChange={(e) => setBookingDate(e.target.value)}
                readOnly={isSingleDayWindow}
                data-testid="storefront-buy-booking-date"
                className={`w-full min-w-0 border-2 border-ink px-2 py-2 text-sm bg-white min-h-[40px] ${isSingleDayWindow ? "cursor-not-allowed opacity-90" : ""}`}
                style={{ WebkitAppearance: "none" }}
                title={isSingleDayWindow ? "This service is only available on this date." : undefined}
              />
              {isSingleDayWindow && (
                <p className="text-[10px] text-ink/60 mt-1">
                  This service is only available on this date.
                </p>
              )}
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider block mb-1">Time slot</label>
              <select
                required
                value={bookingSlot}
                onChange={(e) => setBookingSlot(e.target.value)}
                data-testid="storefront-buy-booking-slot"
                className="w-full border-2 border-ink px-2 py-2 text-sm bg-white min-h-[40px]"
              >
                <option value="">Pick a time…</option>
                {availableSlots.map((s) => {
                  const st = slotStatus.get(s);
                  const full = st?.full;
                  const remaining = st?.remaining;
                  const label = full
                    ? `${s} — Full`
                    : (remaining != null && remaining <= 3
                        ? `${s} — ${remaining} left`
                        : s);
                  return (
                    <option key={s} value={s} disabled={full}>{label}</option>
                  );
                })}
              </select>
              {slotAvail?.max_per_slot ? (
                <p className="text-[10px] text-ink/60 mt-1" data-testid="storefront-buy-slot-cap-hint">
                  Up to {slotAvail.max_per_slot} bookings per slot.
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold flex-shrink-0">Units</label>
            <input
              type="number" min={1} max={100}
              value={units}
              onChange={(e) => setUnits(e.target.value)}
              data-testid="storefront-buy-units"
              className="w-24 border-2 border-ink px-3 py-2 text-sm"
            />
          </div>
        )}
        <input
          type="email" required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          autoComplete="email"
          data-testid="storefront-buy-email"
          className="w-full border-2 border-ink px-3 py-2 text-sm"
        />
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          maxLength={80}
          data-testid="storefront-buy-name"
          className="w-full border-2 border-ink px-3 py-2 text-sm"
        />
        {/* v146.2 — When the block requires a phone and the OTP flow
            isn't already showing a phone field, expose one directly in
            the form. `otpStage !== "skip"` means the dedicated OTP
            phone input is already visible below.
            v147.99.6 — Split country-code + national-number picker so
            buyer_phone lands in the DB as canonical E.164
            ("+91XXXXXXXXXX") — kills the 91-vs-no-91 duplicate customer
            records the brand's coin ledger was accumulating. */}
        {(requirePhone || paymentMethod === "cod") && otpStage === "skip" && (
          <PhoneCountryInput
            value={phone}
            onChange={setPhone}
            placeholder={requirePhone ? "Phone number (required)" : "Phone number"}
            required={requirePhone}
            testidPrefix="storefront-buy-phone-inline"
          />
        )}
        {!isService && (
          <textarea
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder={(requireAddress || paymentMethod === "cod")
              ? "Shipping / delivery address (required)"
              : "Shipping / delivery address (optional)"}
            maxLength={500}
            rows={2}
            required={requireAddress && !isService}
            data-testid="storefront-buy-address"
            className="w-full border-2 border-ink px-3 py-2 text-sm"
          />
        )}
        {product.instructions && (
          <div className="border-2 border-ink bg-cream p-3" data-testid="storefront-buy-instructions">
            <p className="text-[10px] font-bold uppercase tracking-wider mb-1">
              {isService ? "Booking terms & instructions" : "Instructions & terms"}
            </p>
            <p className="text-xs text-ink/80 whitespace-pre-wrap">{product.instructions}</p>
          </div>
        )}
        {product.social_link && (
          <div className="border-2 border-ink shadow-brutalSm" data-testid="storefront-buy-reel-embed-wrap">
            <InlineVideoEmbed
              url={product.social_link}
              testid="storefront-buy-reel-embed"
              productId={product.product_id}
              surface="buy_modal"
            />
          </div>
        )}

        {/* v108 — Have a coupon? Visitors enter the code and we validate
            server-side. Only creator-opted-in coupons will apply.
            v145.61 — Suppressed entirely when the creator has already
            passed on a Linkola discount (decision 4c: no stacking). */}
        {!hasCreatorDiscount && (
        <div className="border-2 border-ink bg-cream p-3 space-y-2" data-testid="storefront-buy-coupon-panel">
          <p className="text-[11px] font-bold uppercase tracking-wider">Have a coupon?</p>
          {coupon ? (
            <div className="flex items-start gap-2" data-testid="storefront-buy-coupon-applied">
              <div className="flex-1 min-w-0">
                <p className="font-mono font-bold text-sm">{coupon.code}</p>
                <p className="text-[11px] text-mint font-bold">
                  {coupon.discount_pct}% off · You save ₹{Number(coupon.discount_inr).toFixed(2)}
                </p>
                {coupon.description && (
                  <p className="text-[10px] text-ink/60 truncate">{coupon.description}</p>
                )}
              </div>
              <button
                type="button"
                onClick={removeCoupon}
                data-testid="storefront-buy-coupon-remove"
                className="border-2 border-ink bg-white px-2 py-1 text-[11px] font-bold shadow-brutalSm inline-flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Remove
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Enter code (e.g. SAVE20)"
                maxLength={30}
                data-testid="storefront-buy-coupon-input"
                className="flex-1 border-2 border-ink px-3 py-2 font-mono text-sm bg-white uppercase"
              />
              <button
                type="button"
                onClick={applyCoupon}
                disabled={applyingCoupon || !couponCode.trim()}
                data-testid="storefront-buy-coupon-apply"
                className="border-2 border-ink bg-ink text-white px-3 py-2 text-xs font-bold shadow-brutalSm disabled:opacity-60"
              >
                {applyingCoupon ? "..." : "Apply"}
              </button>
            </div>
          )}
          {couponError && (
            <p className="text-[11px] font-bold text-coral" data-testid="storefront-buy-coupon-error">
              {couponError}
            </p>
          )}
        </div>
        )}

        {!hasCreatorDiscount && (
        <LoyaltyCoinPanel
          product={product}
          subtotalAfterCoupon={subtotalAfterCoupon}
          coinsToApply={coinsToApply}
          setCoinsToApply={setCoinsToApply}
          coinPreview={coinPreview}
          setCoinPreview={setCoinPreview}
        />
        )}

        {/* v101 — Phone + OTP gate. Only shown when the creator has
            require_phone_otp turned on. Buyer must Send + Verify OTP
            before the Pay button unlocks. */}
        {effectiveRequireOtp && (
          <div className="border-2 border-ink bg-cream p-3 space-y-2" data-testid="storefront-buy-otp-panel">
            <p className="text-[11px] font-bold uppercase tracking-wide">
              Phone verification required
            </p>
            <div className="flex gap-2 items-stretch">
              {/* v146.68 — Split country-code + 10-digit input so the
                  buyer isn't guessing whether to prepend "+91". Trigger
                  is compact ("🇮🇳 +91") for mobile fit. */}
              <div className="flex-1 min-w-0">
                <PhoneCodeInput
                  value={phone}
                  onChange={setPhone}
                  disabled={otpStage === "entering" || otpStage === "verifying" || otpStage === "verified"}
                  testid="storefront-buy-phone"
                  autoFocus={false}
                />
              </div>
              {otpStage !== "verified" && (
                <button
                  type="button"
                  onClick={sendOtp}
                  disabled={otpStage === "sending" || otpStage === "verifying"}
                  data-testid="storefront-buy-send-otp"
                  className="border-2 border-ink bg-white px-3 py-2 font-bold text-xs shadow-brutalSm disabled:opacity-60 shrink-0"
                >
                  {otpStage === "sending" ? <Loader2 className="w-3 h-3 animate-spin" />
                    : otpStage === "entering" ? "Resend" : "Send OTP"}
                </button>
              )}
            </div>
            {(otpStage === "entering" || otpStage === "verifying") && (
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  placeholder={`Enter 6-digit OTP (${otpChannelSent || "SMS/WhatsApp"})`}
                  maxLength={8}
                  data-testid="storefront-buy-otp-code"
                  className="flex-1 border-2 border-ink px-3 py-2 text-sm bg-white font-mono"
                />
                <button
                  type="button"
                  onClick={verifyOtp}
                  disabled={otpStage === "verifying"}
                  data-testid="storefront-buy-verify-otp"
                  className="border-2 border-ink bg-coral text-white px-3 py-2 font-bold text-xs shadow-brutalSm disabled:opacity-60"
                >
                  {otpStage === "verifying" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Verify"}
                </button>
              </div>
            )}
            {otpStage === "verified" && (
              <p className="text-xs text-mint font-bold inline-flex items-center gap-1" data-testid="storefront-buy-otp-verified">
                <Check className="w-3 h-3" /> Phone verified — you can proceed
              </p>
            )}
          </div>
        )}

        {error && <p className="text-xs text-coral font-bold" data-testid="storefront-buy-error">{error}</p>}

        {/* v146 — Payment method selector. Only rendered when the
            creator has enabled COD in Settings → Commerce & Payments.
            Server re-checks the plan gate on submit so a spoofed
            `payment_method=cod` on a non-Brand creator returns 402. */}
        {codOnly && (
          <div className="border-2 border-ink bg-cream p-2" data-testid="storefront-buy-payment-method">
            <p className="text-[11px] font-bold" data-testid="storefront-buy-cod-only">
              Cash on Delivery
              <span className="block font-normal text-ink/70 mt-0.5">
                This seller takes payment in cash at delivery / pickup.
              </span>
            </p>
          </div>
        )}
        {codEnabled && !codOnly && (
          <div className="border-2 border-ink bg-cream p-2" data-testid="storefront-buy-payment-method">
            <p className="text-[10px] font-black uppercase tracking-wider mb-1.5">
              How do you want to pay?
            </p>
            <div className="flex gap-1.5">
              {[
                { key: "razorpay", label: "Pay Now" },
                { key: "cod",      label: "Cash on Delivery" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => { setPaymentMethod(opt.key); setError(""); }}
                  data-testid={`storefront-buy-pm-${opt.key}`}
                  aria-pressed={paymentMethod === opt.key}
                  className={`flex-1 border-2 border-ink px-2 py-1.5 text-[11px] font-bold ${
                    paymentMethod === opt.key
                      ? "bg-ink text-white"
                      : "bg-white hover:bg-cream"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {paymentMethod === "cod" && (
              <p className="text-[10px] text-ink/60 mt-1.5 leading-snug">
                Pay the seller in cash at delivery / pickup. Your order stays as <span className="border border-ink px-1">Pending</span> until the seller confirms.
              </p>
            )}
          </div>
        )}

        {/* v146.42 — Unified coin earn strip. When the buyer already
            holds a balance with this creator, we swap the "Earn N"
            copy for "Balance N → M coins after this" so returning
            customers see their loyalty flywheel build up.
            v147.60 — Applies uniformly to products, services, AND
            tickets (all use this modal). Hidden on COD because the
            seller credits coins manually via bill-entry after the
            physical payment settles — auto-crediting on order create
            would let buyers earn on unpaid orders.
            v147.66 — Public fallback via `useCreatorLoyalty`: tickets
            are typically bought by anon buyers (phone-OTP, no app
            login), so `coinPreview` never loads and the yellow chip
            never showed. The fallback reads the creator's public
            engagement config so the chip renders for anyone, matching
            the storefront card behaviour. This is the whole point of
            surfacing the reward — nudge buyers to pay online instead
            of COD (which forfeits the coins). */}
        {paymentMethod !== "cod" && total > 0 && (
          <CheckoutEarnPill
            slug={slug}
            amountInr={total}
            coinPreview={coinPreview}
          />
        )}
        {/* v147.60 — COD earn-later hint. Reassures buyers they still
            earn coins on their bill, but crediting waits until the
            seller confirms payment (via manual bill entry). */}
        {paymentMethod === "cod" && total > 0 && (
          <CheckoutCodEarnHint slug={slug} coinPreview={coinPreview} />
        )}
        {/* v147.53 — Availability window banner + button lock-out.
            v147.54 — When ending today, swap to a live countdown chip
            so buyers feel the urgency. */}
        {countdown ? (
          <p
            className={`text-xs font-black uppercase tracking-wider inline-flex items-center gap-1 px-2 py-1 border-2 border-ink self-start ${
              countdown.urgent ? "bg-coral text-white animate-pulse" : "bg-sun"
            }`}
            data-testid="storefront-buy-countdown"
            aria-live="polite"
          >
            ⏰ {countdown.label}
          </p>
        ) : availabilityLabel ? (
          <p
            className={`text-xs font-semibold ${
              availabilityBlocked ? "text-coral" : "text-ink/70"
            }`}
            data-testid="storefront-buy-availability-label"
          >
            {availabilityLabel}
          </p>
        ) : null}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={step === "processing" || step === "cod_success" || step === "success" || availabilityBlocked || isTicketSoldOut}
            data-testid="storefront-buy-pay-btn"
            className="flex-1 border-2 border-ink bg-coral text-white px-3 py-2 font-bold text-sm shadow-brutalSm inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {step === "processing" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            {availabilityBlocked
              ? (winState.status === "ended"
                  ? (isService ? "Bookings closed" : isTicket ? "Ticket sales closed" : "Sale ended")
                  : (isService ? `Bookings open ${formatWinDate(winState.start)}` : `Available ${formatWinDate(winState.start)}`))
              : isTicketSoldOut
                ? "Sold out"
                : paymentMethod === "cod"
                  ? `${isService ? "Book" : "Place order"} · Pay ₹${grandTotal.toFixed(2)} at delivery`
                  : `${isTicket ? `Get ${effectiveUnits} ticket${effectiveUnits === 1 ? "" : "s"} · Pay` : (isService ? "Book & pay" : "Pay")} ₹${grandTotal.toFixed(2)}`}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            data-testid="storefront-buy-cancel-btn"
            className="border-2 border-ink bg-white px-3 py-2 font-bold text-sm shadow-brutalSm"
          >
            Cancel
          </button>
        </div>
        <p className="text-[10px] text-ink/50 text-center">
          {paymentMethod === "cod"
            ? "Cash on Delivery · Confirmed by seller"
            : "Powered by Razorpay · Secure checkout"}
        </p>

        {/* v146 — COD confirmation.
            v149.36 — Was an `absolute inset-0` overlay INSIDE this
            scrolling form, so on a tall mobile checkout it was laid out
            against the full scroll height: a buyer who had scrolled down
            to reach the button was left staring at blank space with the
            confirmation somewhere above or below. It now uses the same
            fixed full-screen `CheckoutSuccessScreen` the paid path has
            used since v147.67 — pinned to the top of the viewport, so
            there is nothing to scroll back to — with COD wording and the
            order number. */}
        {step === "cod_success" && (
          <CheckoutSuccessScreen
            cod
            paidOrder={paidOrder}
            product={product}
            isTicket={isTicket}
            isService={isService}
            fallbackAmount={grandTotal}
            fallbackEmail={email}
            fallbackName={name}
            onDone={(e) => { e.stopPropagation(); onClose(); onSuccess && onSuccess(); }}
          />
        )}
        {/* v146.95 — Razorpay success / receipt overlay. Shown after a
            successful online payment so the buyer gets an obvious
            confirmation (order id + amount + "emailed to you" note)
            instead of the modal silently closing.
            v147.67 — Full-screen takeover instead of modal-inner
            overlay so the storefront/bio-hub background (Instagram
            reels, product cards, coupon strips) never bleeds through.
            For tickets we now render the QR + event summary INLINE so
            the buyer can screenshot / show it at the door without a
            second tab bounce. */}
        {step === "success" && (
          <CheckoutSuccessScreen
            paidOrder={paidOrder}
            product={product}
            isTicket={isTicket}
            isService={isService}
            fallbackAmount={grandTotal}
            fallbackEmail={email}
            fallbackName={name}
            onDone={(e) => { e.stopPropagation(); onClose(); onSuccess && onSuccess(); }}
          />
        )}
      </form>
    </div>,
    document.body,
    )
  );
}

export default StorefrontBuyModal;

// ---------------------------------------------------------------------------
// v147.58 — TicketEventPanel
// Shown inside StorefrontBuyModal when product.kind === "ticket". Replaces
// the service date/slot picker with:
//   * Event date / time / venue summary
//   * Live "X tickets left" chip (or SOLD OUT badge)
//   * Quantity stepper (± buttons) bounded by max_per_order + tickets_left
// ---------------------------------------------------------------------------
function TicketEventPanel({
  product, tiers, selectedTierId, onSelectTier,
  units, setUnits, maxUnits, ticketsLeft, isTicketSoldOut,
  groupPct, groupMin, groupDiscountActive, groupDiscountInr,
}) {
  const fmt = (iso) => {
    if (!iso) return "";
    try {
      const [y, m, d] = iso.split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString(undefined, {
        weekday: "short", month: "short", day: "numeric", year: "numeric",
      });
    } catch { return iso; }
  };
  const isSingleDay = !product.event_date_to
    || product.event_date_from === product.event_date_to;
  const timeLabel = product.event_time
    ? (product.event_end_time
        ? `${product.event_time} – ${product.event_end_time}`
        : product.event_time)
    : null;
  const clampedUnits = Math.max(1, Math.min(maxUnits, Number(units) || 1));
  const hasTiers = Array.isArray(tiers) && tiers.length > 0;
  return (
    <div className="border-2 border-ink bg-cream p-3 space-y-3 overflow-hidden" data-testid="storefront-buy-ticket-panel">
      <p className="text-[10px] font-black uppercase tracking-widest text-ink/60">
        Event
      </p>
      {isSingleDay ? (
        <p className="text-sm font-bold break-words" data-testid="storefront-buy-event-date">
          📅 {fmt(product.event_date_from)}
          {timeLabel ? <span className="font-normal text-ink/70"> · {timeLabel}</span> : null}
        </p>
      ) : (
        <p className="text-sm font-bold break-words" data-testid="storefront-buy-event-date">
          📅 {fmt(product.event_date_from)} → {fmt(product.event_date_to)}
          {timeLabel ? <span className="font-normal text-ink/70"> · {timeLabel} daily</span> : null}
        </p>
      )}
      {product.venue_name && (
        <p className="text-sm break-words" data-testid="storefront-buy-event-venue">
          📍 <b>{product.venue_name}</b>
          {product.venue_address ? <span className="text-ink/70"> · {product.venue_address}</span> : null}
          {product.venue_map_url && (
            <> · <a href={product.venue_map_url} target="_blank" rel="noreferrer" className="underline">Map</a></>
          )}
        </p>
      )}

      {/* v147.59 — Tier picker. Rendered only for multi-tier events. */}
      {hasTiers && (
        <div className="pt-2 border-t-2 border-ink/10 space-y-1.5" data-testid="storefront-buy-tier-picker">
          <p className="text-[10px] font-black uppercase tracking-widest text-ink/60">
            Pick a tier
          </p>
          <div className="space-y-1.5">
            {tiers.map((t) => {
              const left = Math.max(0, Number(t.total_tickets || 0) - Number(t.tickets_sold || 0));
              const sold = left === 0;
              const active = t.tier_id === selectedTierId;
              return (
                <button
                  key={t.tier_id || t.name}
                  type="button"
                  onClick={() => !sold && onSelectTier(t.tier_id)}
                  disabled={sold}
                  data-testid={`storefront-buy-tier-${t.tier_id}`}
                  className={`w-full border-2 border-ink p-2 flex items-center gap-2 text-left transition-colors ${
                    sold ? "bg-ink/5 opacity-70 cursor-not-allowed"
                    : active ? "bg-coral text-white shadow-brutalSm"
                    : "bg-white hover:bg-cream"
                  }`}
                >
                  <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                    active ? "border-white bg-white" : "border-ink"
                  }`}>
                    {active && <span className="block w-full h-full rounded-full scale-50 bg-coral" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-bold truncate">{t.name}</span>
                    {t.description && (
                      <span className={`block text-[11px] truncate ${active ? "text-white/80" : "text-ink/60"}`}>
                        {t.description}
                      </span>
                    )}
                  </span>
                  <span className="text-right flex-shrink-0">
                    <span className="block text-sm font-black tabular-nums">
                      ₹{Number(t.price || 0).toFixed(0)}
                    </span>
                    <span className={`block text-[10px] uppercase font-bold ${
                      sold ? "text-coral" : left <= 10 ? "text-ink/70" : "text-ink/50"
                    }`}>
                      {sold ? "Sold out" : `${left} left`}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-2 border-t-2 border-ink/10 flex-wrap">
        {!hasTiers && (
          <span
            className={`text-[11px] font-black uppercase tracking-wider px-2 py-1 border-2 border-ink ${
              isTicketSoldOut ? "bg-coral text-white"
              : ticketsLeft != null && ticketsLeft <= 10 ? "bg-sun"
              : "bg-mint"
            }`}
            data-testid="storefront-buy-tickets-left"
          >
            {isTicketSoldOut
              ? "Sold out"
              : ticketsLeft != null
                ? `${ticketsLeft} tickets left`
                : "Tickets available"}
          </span>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <label className="text-xs font-bold">Qty</label>
          <button
            type="button"
            onClick={() => setUnits(Math.max(1, clampedUnits - 1))}
            disabled={isTicketSoldOut || clampedUnits <= 1}
            data-testid="storefront-buy-ticket-qty-minus"
            className="w-9 h-9 border-2 border-ink bg-white font-bold disabled:opacity-40"
          >−</button>
          <input
            type="number"
            min={1}
            max={maxUnits}
            value={clampedUnits}
            onChange={(e) => setUnits(Math.max(1, Math.min(maxUnits, Number(e.target.value) || 1)))}
            data-testid="storefront-buy-ticket-qty"
            className="w-14 border-2 border-ink px-2 py-1 text-sm text-center font-bold min-w-0"
            inputMode="numeric"
          />
          <button
            type="button"
            onClick={() => setUnits(Math.min(maxUnits, clampedUnits + 1))}
            disabled={isTicketSoldOut || clampedUnits >= maxUnits}
            data-testid="storefront-buy-ticket-qty-plus"
            className="w-9 h-9 border-2 border-ink bg-white font-bold disabled:opacity-40"
          >+</button>
        </div>
      </div>

      {/* v147.59 — Group-discount live nudge. Rendered only when the
          creator has configured a discount (this component is only
          mounted for tickets so we don't re-check `isTicket`). */}
      {groupPct > 0 && groupMin >= 2 && (
        <p
          className={`text-[11px] font-black uppercase tracking-wider inline-flex items-center gap-1 px-2 py-1 border-2 border-ink self-start ${
            groupDiscountActive ? "bg-mint" : "bg-white"
          }`}
          data-testid="storefront-buy-group-discount"
        >
          {groupDiscountActive
            ? `🎁 Group discount applied — you saved ₹${Number(groupDiscountInr).toFixed(0)} (${groupPct}% off)`
            : `🎁 Add ${Math.max(1, groupMin - clampedUnits)} more to unlock ${groupPct}% off`}
        </p>
      )}

      <p className="text-[10px] text-ink/60">
        Max {maxUnits} per order · Phone-OTP verification required at checkout.
      </p>
    </div>
  );
}


// ---------------------------------------------------------------------------
// v147.66 — CheckoutEarnPill
// Yellow "Earn N coins on this purchase" chip for the checkout modal.
// Prefers the authenticated `coinPreview` (carries the buyer's real balance
// so we can render "N → M after this"), and falls back to the public
// `useCreatorLoyalty` engagement config for anon buyers — critical for
// tickets, which are typically bought via phone-OTP without an app login.
// ---------------------------------------------------------------------------
function CheckoutEarnPill({ slug, amountInr, coinPreview }) {
  const publicLoyalty = useCreatorLoyalty(slug);
  const authedRate = Number(coinPreview?.earn_purchase_per_100 || 0);
  const publicRate = Number(publicLoyalty?.earn_rules?.purchase_per_100 || 0);
  const rate = authedRate > 0 ? authedRate : publicRate;
  if (rate <= 0) return null;
  const coins = Math.floor(Math.max(0, Number(amountInr) || 0) / 100) * rate;
  if (coins <= 0) return null;
  const coinName = coinPreview?.coin_name || publicLoyalty?.coin_name || "coins";
  const balance = coinPreview
    ? Number(coinPreview.balance || 0)
    : Number(publicLoyalty?.balance || 0);
  return (
    <CoinEarnChip
      coins={coins}
      currentBalance={balance}
      coinName={coinName}
      size="md"
      testId="storefront-buy-earn-pill"
    />
  );
}

// ---------------------------------------------------------------------------
// v147.66 — CheckoutCodEarnHint
// Neutral dashed strip explaining that COD buyers won't auto-earn coins —
// the seller credits them manually via the bill-entry flow. Nudges buyers
// toward paying online (where they DO earn coins automatically).
// ---------------------------------------------------------------------------
function CheckoutCodEarnHint({ slug, coinPreview }) {
  const publicLoyalty = useCreatorLoyalty(slug);
  const rate = Number(coinPreview?.earn_purchase_per_100
    || publicLoyalty?.earn_rules?.purchase_per_100 || 0);
  if (rate <= 0) return null;
  const coinName = coinPreview?.coin_name || publicLoyalty?.coin_name || "coins";
  return (
    <p
      className="text-[11px] font-bold text-ink border-2 border-dashed border-ink/70 bg-sun px-2 py-1.5"
      data-testid="storefront-buy-earn-cod-hint"
    >
      On COD, the seller credits {coinName} manually after confirming your bill.
    </p>
  );
}


// ---------------------------------------------------------------------------
// v147.67 — CheckoutSuccessScreen
// Full-screen takeover shown after a successful payment. Renders a clean
// receipt with the order summary, and — for tickets — an inline QR + event
// summary so the buyer can screenshot / show the QR at the door without
// having to visit the standalone /tickets/{orderId} page.
// Uses `fixed inset-0 bg-cream z-[10001]` so nothing from the storefront
// (video reels, product cards, coupon strips) bleeds through.
// ---------------------------------------------------------------------------
function CheckoutSuccessScreen({
  paidOrder, product, isTicket, isService, fallbackAmount, fallbackEmail, fallbackName, onDone,
  // v149.36 — Cash on Delivery variant. Nothing has been collected and
  // the seller hasn't confirmed yet, so the wording says so and the
  // delivery QR is withheld: a COD order sits at `status: pending`
  // until the seller confirms, and /scan refuses to scan one.
  cod = false,
}) {
  const orderId = paidOrder?.order_id;
  const buyerEmail = paidOrder?.buyer_email || fallbackEmail || "";
  const buyerName = (paidOrder?.buyer_name || fallbackName || "").trim();
  const amount = Number(paidOrder?.amount_inr ?? fallbackAmount ?? 0);
  const units = Number(paidOrder?.units || 1);
  const tierName = paidOrder?.tier_name;

  // v147.72 — Multi-QR list. Bookings of >1 unit now generate ONE QR
  // per attendee (`LINKOLA-TICKET|{order_id}|{email}|{index}`), matching
  // the door-scan backend that tracks each index separately and only
  // promotes the order to `delivered` when all indexes are checked in.
  // v147.75 — Products and services also render a single QR now so
  // buyers can present it at pickup/delivery for /scan to mark
  // `delivered`. The payload keeps the `LINKOLA-TICKET` prefix so the
  // existing scanner parser accepts it — the backend routes on `kind`.
  const showQr = !cod && !!orderId && (isTicket || (!isService) || isService); // any kind
  const qrList = React.useMemo(() => {
    if (!showQr) return [];
    const count = isTicket ? Math.max(1, units) : 1;
    return Array.from({ length: count }, (_, i) => {
      const idx = i + 1;
      const payload = `LINKOLA-TICKET|${orderId}|${buyerEmail}|${idx}`;
      return {
        index: idx,
        src: `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(payload)}&size=220x220&margin=4`,
      };
    });
  }, [showQr, isTicket, orderId, buyerEmail, units]);

  const ticketUrl = orderId
    ? `/tickets/${encodeURIComponent(orderId)}${buyerEmail ? `?email=${encodeURIComponent(buyerEmail)}` : ""}`
    : null;

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const [y, m, d] = iso.split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString(undefined, {
        weekday: "short", month: "short", day: "numeric", year: "numeric",
      });
    } catch { return iso; }
  };
  const isSingleDay = !product?.event_date_to
    || product?.event_date_from === product?.event_date_to;

  // Belt and braces for mobile Safari: the takeover is `fixed`, so it
  // already covers the viewport whatever the page behind is doing, but
  // reset this element's own scroll too in case it re-mounts scrolled.
  const rootRef = useRef(null);
  useEffect(() => {
    const el = rootRef.current;
    if (el) el.scrollTop = 0;
  }, []);

  return createPortal(
    <div
      ref={rootRef}
      className="fixed inset-0 z-[10001] bg-cream overflow-y-auto"
      data-testid={cod ? "storefront-buy-cod-success" : "storefront-buy-success"}
    >
      <div className="min-h-full flex flex-col items-stretch p-4 sm:p-8">
        <div className="w-full max-w-md mx-auto space-y-3">
          {/* Success banner */}
          <div className="border-4 border-ink bg-mint p-4 space-y-2 shadow-brutal">
            <div className="inline-flex items-center gap-2 border-2 border-ink bg-white px-2 py-0.5 shadow-brutalSm">
              <ClipboardCheck className="w-4 h-4" strokeWidth={3} />
              <span className="text-[10px] font-black uppercase tracking-widest">
                {cod ? "Order placed" : isTicket ? "Ticket confirmed" : "Payment received"}
              </span>
            </div>
            <p className="font-black text-lg leading-tight" data-testid="storefront-buy-success-title">
              {cod
                ? "Order placed — pending confirmation"
                : `Thanks ${buyerName || "for your purchase"}! Your ${isTicket ? "ticket" : "order"} is confirmed.`}
            </p>
            <p className="text-xs text-ink/80" data-testid="storefront-buy-success-body">
              {cod
                ? `The seller has been notified and will confirm your COD order shortly. You'll get a confirmation email at ${buyerEmail || "your email"} once accepted. Keep ₹${amount.toFixed(2)} ready at delivery / pickup — payment is collected only after the seller confirms.`
                : isTicket
                ? `Show the QR below at the entrance. We've also emailed a copy to ${buyerEmail || "your email"}.`
                : isService
                  ? `Your booking for ${product?.title} is locked in. The seller has been notified and will reach out shortly.`
                  : `Your ${product?.title} order has been placed. The seller has been notified and will begin fulfilment.`}
            </p>
          </div>

          {/* Ticket-specific: inline QR grid + event summary */}
          {isTicket && qrList.length > 0 && (
            <div className="border-4 border-ink bg-white p-4 space-y-3 shadow-brutal" data-testid="storefront-buy-success-ticket">
              <p className="text-[10px] font-black uppercase tracking-widest text-ink/60">
                {qrList.length === 1
                  ? `Entry QR${tierName ? ` · ${tierName}` : ""}`
                  : `${qrList.length} entry QRs · one per attendee${tierName ? ` · ${tierName}` : ""}`}
              </p>
              {/* v147.84 — 8-char ticket code chip. Buyer can read this
                  aloud to venue staff when the QR is unreadable. */}
              <div className="flex justify-center">
                <div className="inline-flex items-center gap-2 border-2 border-ink bg-sun px-2.5 py-1 shadow-brutalSm" data-testid="storefront-buy-success-code">
                  <span className="text-[9px] font-black uppercase tracking-widest text-ink/60">Ticket code</span>
                  <span className="font-mono font-black text-sm tracking-widest">
                    {String(orderId).slice(-8).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {qrList.map((q) => (
                  <div
                    key={q.index}
                    className="flex flex-col items-center gap-1 border-2 border-dashed border-ink p-3"
                    data-testid={`storefront-buy-success-qr-slot-${q.index}`}
                  >
                    <p className="text-[10px] font-black uppercase tracking-widest">
                      Ticket {q.index} of {qrList.length}
                    </p>
                    <img
                      src={q.src}
                      alt={`Ticket ${q.index} QR`}
                      className="w-44 h-44 border-2 border-ink"
                      data-testid={q.index === 1 ? "storefront-buy-success-qr" : `storefront-buy-success-qr-${q.index}`}
                    />
                    <p className="text-[10px] font-mono text-ink/50 break-all text-center">
                      {orderId}#{q.index}
                    </p>
                  </div>
                ))}
              </div>
              {qrList.length > 1 && (
                <p className="text-[11px] text-ink/60 text-center">
                  Share one QR with each attendee. The order shows as <b>delivered</b> once every QR is scanned at the door.
                </p>
              )}
              {(product?.event_date_from || product?.venue_name) && (
                <div className="border-t-2 border-dashed border-ink pt-3 space-y-1.5 text-sm">
                  {product?.event_date_from && (
                    <p className="font-bold">
                      📅 {isSingleDay
                        ? fmtDate(product.event_date_from)
                        : `${fmtDate(product.event_date_from)} → ${fmtDate(product.event_date_to)}`}
                      {product.event_time
                        ? <span className="font-normal text-ink/70"> · {product.event_time}{product.event_end_time ? ` – ${product.event_end_time}` : ""}</span>
                        : null}
                    </p>
                  )}
                  {product?.venue_name && (
                    <p className="text-ink/80">
                      📍 <b>{product.venue_name}</b>
                      {product.venue_address ? <span className="text-ink/60"> · {product.venue_address}</span> : null}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* v147.75 — Generic product / service delivery QR. Buyer
              presents this at pickup / delivery for the seller's /scan
              app to mark the order as `delivered`. */}
          {!isTicket && qrList.length > 0 && (
            <div
              className="border-4 border-ink bg-white p-4 space-y-3 shadow-brutal"
              data-testid="storefront-buy-success-delivery"
            >
              <p className="text-[10px] font-black uppercase tracking-widest text-ink/60 text-center">
                {isService ? "Booking QR" : "Delivery QR"}
              </p>
              {/* v147.84 — 8-char code chip so the buyer can read it if
                  the QR won't scan at pickup / appointment. */}
              <div className="flex justify-center">
                <div className="inline-flex items-center gap-2 border-2 border-ink bg-sun px-2.5 py-1 shadow-brutalSm" data-testid="storefront-buy-success-delivery-code">
                  <span className="text-[9px] font-black uppercase tracking-widest text-ink/60">
                    {isService ? "Booking code" : "Order code"}
                  </span>
                  <span className="font-mono font-black text-sm tracking-widest">
                    {String(orderId).slice(-8).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="flex flex-col items-center gap-1 border-2 border-dashed border-ink p-3">
                <img
                  src={qrList[0].src}
                  alt="Delivery QR"
                  className="w-44 h-44 border-2 border-ink"
                  data-testid="storefront-buy-success-delivery-qr"
                />
                <p className="text-[10px] font-mono text-ink/50 break-all text-center">
                  {orderId}
                </p>
              </div>
              <p className="text-[11px] text-ink/60 text-center">
                {isService
                  ? "Show this QR to the seller at your appointment. They'll scan it to mark your booking as delivered."
                  : "Show this QR at pickup / delivery. The seller scans it to confirm you've received the order."}
              </p>
            </div>
          )}

          {/* Order summary */}
          <div className="border-2 border-ink bg-white p-4 space-y-1.5 text-xs font-mono">
            <div className="flex justify-between gap-2">
              <span className="text-ink/60">Order ID</span>
              <span className="font-bold truncate max-w-[60%]" data-testid="storefront-buy-success-order-id">
                {orderId || "—"}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-ink/60">{cod ? "Pay at delivery" : "Amount paid"}</span>
              <span className="font-bold" data-testid="storefront-buy-success-amount">
                ₹{amount.toFixed(2)}
              </span>
            </div>
            {paidOrder?.razorpay_payment_id && (
              <div className="flex justify-between gap-2">
                <span className="text-ink/60">Payment ref</span>
                <span className="font-bold truncate max-w-[60%]">{paidOrder.razorpay_payment_id}</span>
              </div>
            )}
          </div>

          <div className="border-2 border-dashed border-ink bg-cream/60 p-3 flex items-start gap-2" data-testid="storefront-buy-success-email-note">
            <Mail className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={2.5} />
            <p className="text-[11px] leading-snug">
              A tax invoice + receipt has been emailed to <b>{buyerEmail || "your email"}</b>. Keep it for your records.
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2 pt-1">
            {isTicket && ticketUrl && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  // v147.67 — Open the full ticket page in a NEW tab
                  // (via window.open which is not blocked by popup
                  // rules when triggered from a user gesture, and
                  // survives target="_blank" hardening on iOS). Falls
                  // back to same-tab navigation if the popup is
                  // blocked so the buyer never gets stuck.
                  const win = window.open(ticketUrl, "_blank", "noopener,noreferrer");
                  if (!win) window.location.href = ticketUrl;
                }}
                data-testid="storefront-buy-view-ticket-btn"
                className="border-2 border-ink bg-coral text-white px-3 py-3 font-bold text-sm shadow-brutalSm text-center inline-flex items-center justify-center gap-2"
              >
                🎟 View / Download Ticket
              </button>
            )}
            <button
              type="button"
              onClick={onDone}
              data-testid="storefront-buy-success-done-btn"
              className="border-2 border-ink bg-ink text-white px-3 py-3 font-bold text-sm shadow-brutalSm hover:translate-y-[1px]"
            >
              Done
            </button>
          </div>
          <p className="text-[10px] text-ink/40 text-center pt-2">
            Powered by Linkola · Secure checkout
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
